const HS_UPSELL_STORAGE_PREFIX = 'hs-upsell';
const HS_UPSELL_EXCLUDED_TEXT = ['clip', 'hairclip', 'קליפס'];
const HS_UPSELL_FALLBACKS = {
  default: {
    add: ['trucker-cap', 'speed-of-light-polished-black-copy', 'speed-of-light-2-0-sunset'],
    checkout: ['washed-black-tshirt', 'swim-shorts-red-white', 'button-shirt', 'raw-jorts'],
  },
  swim: {
    add: ['speed-of-light-2-0-brown', 'trucker-cap'],
    checkout: ['washed-black-tshirt', 'button-shirt', 'raw-jorts'],
  },
  sunglasses: {
    add: ['trucker-cap', 'swim-shorts-red-white'],
    checkout: ['washed-black-tshirt', 'raw-jorts', 'button-shirt'],
  },
  cap: {
    add: ['speed-of-light-2-0-sunset', 'washed-black-tshirt'],
    checkout: ['swim-shorts-red-white', 'button-shirt', 'raw-jorts'],
  },
  denim: {
    add: ['washed-black-tshirt', 'speed-of-light-2-0-brown'],
    checkout: ['trucker-cap', 'swim-shorts-red-white'],
  },
};

function hsNormalizeId(value) {
  return String(value || '').replace('gid://shopify/Product/', '');
}

function hsStorageKey(stage, token) {
  return HS_UPSELL_STORAGE_PREFIX + ':' + stage + ':' + (token || 'session');
}

function hsStorageGet(key) {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function hsStorageSet(key, value) {
  try {
    sessionStorage.setItem(key, value);
  } catch {}
}

function hsEscape(value) {
  const entities = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  };

  return String(value || '').replace(/[&<>"']/g, (character) => entities[character]);
}

function hsPriceInCents(value) {
  const number = Number(value || 0);

  if (number > 0 && number < 1000 && String(value).includes('.')) {
    return Math.round(number * 100);
  }

  return Math.round(number);
}

function hsFormatMoney(cents) {
  const language = (document.documentElement.lang || '').toLowerCase().startsWith('he') ? 'he-IL' : 'en-IL';

  try {
    return new Intl.NumberFormat(language, {
      style: 'currency',
      currency: 'ILS',
      maximumFractionDigits: 0,
    }).format(Number(cents || 0) / 100);
  } catch {
    return Math.round(Number(cents || 0) / 100) + ' ₪';
  }
}

function hsProductText(product) {
  return (String(product?.title || '') + ' ' + String(product?.handle || '')).toLowerCase();
}

function hsIsExcludedProduct(product) {
  const text = hsProductText(product);
  return HS_UPSELL_EXCLUDED_TEXT.some((word) => text.includes(word));
}

function hsIsProductInCart(product, cart) {
  const productId = hsNormalizeId(product?.id);
  const handle = product?.handle;

  return (cart?.items || []).some((item) => {
    return (
      (productId && hsNormalizeId(item.product_id) === productId) ||
      (handle && item.handle === handle)
    );
  });
}

function hsAvailableVariants(product) {
  return (Array.isArray(product?.variants) ? product.variants : [])
    .filter((variant) => variant && variant.available !== false)
    .map((variant) => ({
      id: hsNormalizeId(variant.id),
      title: variant.title || '',
      price: hsPriceInCents(variant.price || product.price),
    }))
    .filter((variant) => variant.id);
}

function hsOfferFromProduct(product, cart) {
  if (!product || product.available === false || hsIsExcludedProduct(product) || hsIsProductInCart(product, cart)) {
    return null;
  }

  const variants = hsAvailableVariants(product);

  if (!variants.length) {
    return null;
  }

  const id = hsNormalizeId(product.id || product.handle);

  return {
    id,
    handle: product.handle || '',
    title: product.title || '',
    url: product.url || (product.handle ? '/products/' + product.handle : '#'),
    image: product.featured_image || product.images?.[0] || '',
    variants,
  };
}

function hsSourceItem(cart, productId) {
  const targetId = hsNormalizeId(productId);

  for (let index = (cart?.items || []).length - 1; index >= 0; index -= 1) {
    const item = cart.items[index];

    if (targetId && hsNormalizeId(item.product_id) === targetId) {
      return item;
    }
  }

  return cart?.items?.[cart.items.length - 1] || null;
}

function hsCategory(sourceItem) {
  const text = (
    String(sourceItem?.handle || '') +
    ' ' +
    String(sourceItem?.product_title || '') +
    ' ' +
    String(sourceItem?.variant_title || '')
  ).toLowerCase();

  if (text.includes('swim')) return 'swim';
  if (text.includes('sunglass') || text.includes('speed-of-light')) return 'sunglasses';
  if (text.includes('cap') || text.includes('trucker')) return 'cap';
  if (text.includes('jort') || text.includes('cargo') || text.includes('denim') || text.includes('short')) return 'denim';

  return 'default';
}

async function hsGetCart() {
  const response = await fetch('/cart.js', {
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error('Cart request failed');
  }

  return response.json();
}

async function hsGetRecommendations(productId) {
  if (!productId) return [];

  const response = await fetch(
    '/recommendations/products.json?product_id=' +
      encodeURIComponent(hsNormalizeId(productId)) +
      '&intent=complementary&limit=6',
    { headers: { Accept: 'application/json' } }
  );

  if (!response.ok) return [];

  const data = await response.json();
  return Array.isArray(data.products) ? data.products : [];
}

async function hsGetProduct(handle) {
  if (!handle) return null;

  const response = await fetch('/products/' + encodeURIComponent(handle) + '.js', {
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) return null;

  return response.json();
}

function hsCollectOffers(products, cart, excludedKeys) {
  const excluded = new Set((excludedKeys || []).map((key) => hsNormalizeId(key)));
  const seen = new Set();
  const offers = [];

  for (const product of products || []) {
    const offer = hsOfferFromProduct(product, cart);

    if (!offer || excluded.has(hsNormalizeId(offer.id)) || (offer.handle && excluded.has(offer.handle))) {
      continue;
    }

    const key = offer.id || offer.handle;

    if (seen.has(key)) {
      continue;
    }

    seen.add(key);
    offers.push(offer);
  }

  return offers;
}

async function hsGetOffers(productId, cart, stage, excludedKeys) {
  let recommended = [];

  try {
    recommended = await hsGetRecommendations(productId);
  } catch {}

  let offers = hsCollectOffers(recommended, cart, excludedKeys);

  if (offers.length < 2) {
    const source = hsSourceItem(cart, productId);
    const category = hsCategory(source);
    const fallbackConfig = HS_UPSELL_FALLBACKS[category] || HS_UPSELL_FALLBACKS.default;
    const handles = fallbackConfig[stage] || HS_UPSELL_FALLBACKS.default[stage];
    const fallbackProducts = await Promise.all(handles.map((handle) => hsGetProduct(handle).catch(() => null)));
    offers = hsCollectOffers(recommended.concat(fallbackProducts.filter(Boolean)), cart, excludedKeys);
  }

  return offers.slice(0, 2);
}

function hsDispatchCartUpdate(cart, sourceId) {
  document.dispatchEvent(
    new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cart,
        sourceId,
        data: {
          itemCount: cart.item_count,
          source: sourceId,
          sections: {},
        },
      },
    })
  );
}

function hsCloseDialog(dialog) {
  if (!dialog?.open) return;

  let finished = false;
  const finish = (event) => {
    if (event && event.target !== dialog) return;
    if (finished) return;

    finished = true;
    dialog.removeEventListener('animationend', finish);
    dialog.close();
    dialog.classList.remove('dialog-closing');
  };

  dialog.classList.add('dialog-closing');
  dialog.addEventListener('animationend', finish);
  window.setTimeout(finish, 260);
}

function hsOfferMarkup(offer, labels) {
  const selectedVariant = offer.variants[0];
  const hasVariantChoice =
    offer.variants.length > 1 &&
    offer.variants.some((variant) => variant.title && variant.title !== 'Default Title');
  const imageMarkup = offer.image
    ? '<img src="' +
      hsEscape(offer.image) +
      '" width="520" height="520" loading="lazy" alt="' +
      hsEscape(offer.title) +
      '">'
    : '<span class="hs-upsell-card__image-placeholder" aria-hidden="true"></span>';
  const variantMarkup = hasVariantChoice
    ? '<label class="hs-upsell-card__variant"><span class="visually-hidden">' +
      hsEscape(labels.optionLabel) +
      '</span><select data-upsell-variant="' +
      hsEscape(offer.id) +
      '">' +
      offer.variants
        .map(
          (variant) =>
            '<option value="' +
            hsEscape(variant.id) +
            '">' +
            hsEscape(variant.title || labels.selectOption) +
            '</option>'
        )
        .join('') +
      '</select></label>'
    : '';

  return (
    '<article class="hs-upsell-card" data-upsell-card="' +
    hsEscape(offer.id) +
    '">' +
    '<a class="hs-upsell-card__image" href="' +
    hsEscape(offer.url) +
    '" aria-label="' +
    hsEscape(offer.title) +
    '">' +
    imageMarkup +
    '</a>' +
    '<div class="hs-upsell-card__body">' +
    '<a class="hs-upsell-card__title" href="' +
    hsEscape(offer.url) +
    '">' +
    hsEscape(offer.title) +
    '</a>' +
    '<span class="hs-upsell-card__price" data-upsell-price="' +
    hsEscape(offer.id) +
    '">' +
    hsFormatMoney(selectedVariant.price) +
    '</span>' +
    variantMarkup +
    '<button type="button" class="button hs-upsell-card__add" data-upsell-add data-offer-id="' +
    hsEscape(offer.id) +
    '" data-variant-id="' +
    hsEscape(selectedVariant.id) +
    '">' +
    hsEscape(labels.add) +
    '</button>' +
    '</div>' +
    '</article>'
  );
}

function hsRenderOfferList(container, offers, labels) {
  if (!container) return;

  container.innerHTML = offers.map((offer) => hsOfferMarkup(offer, labels)).join('');

  container.querySelectorAll('[data-upsell-variant]').forEach((select) => {
    select.addEventListener('change', () => {
      const card = select.closest('[data-upsell-card]');
      const offer = offers.find((item) => hsNormalizeId(item.id) === hsNormalizeId(select.dataset.upsellVariant));
      const variant = offer?.variants.find((item) => hsNormalizeId(item.id) === hsNormalizeId(select.value));

      if (!card || !variant) return;

      const button = card.querySelector('[data-upsell-add]');
      const price = card.querySelector('[data-upsell-price]');

      if (button) button.dataset.variantId = variant.id;
      if (price) price.textContent = hsFormatMoney(variant.price);
    });
  });
}

class UpsellPopup extends HTMLElement {
  constructor() {
    super();
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleProductFormSubmit = this.handleProductFormSubmit.bind(this);
    this.shownToken = null;
  }

  connectedCallback() {
    document.addEventListener('cart:update', this.handleCartUpdate);
    document.addEventListener('submit', this.handleProductFormSubmit, true);
    this.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:update', this.handleCartUpdate);
    document.removeEventListener('submit', this.handleProductFormSubmit, true);
    this.removeEventListener('click', this.handleClick);
  }

  async handleProductFormSubmit(event) {
    const form = event.target;

    if (!(form instanceof HTMLFormElement) || form.dataset.type !== 'add-to-cart-form') return;

    const variantInput = form.querySelector('input[name="id"]');

    if (variantInput?.value) return;

    const productForm = form.closest('product-form-component');
    const productUrl = productForm?.dataset.productUrl || window.location.pathname;
    const productMatch = productUrl.match(/\/products\/([^/?#]+)/);

    if (!productMatch) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const addButton = form.querySelector('button[name="add"], input[name="add"]');

    if (addButton?.disabled) return;

    if (addButton) addButton.disabled = true;

    try {
      const product = await hsGetProduct(productMatch[1]);
      const variant = hsAvailableVariants(product).find((item) => item.id);

      if (!variant) throw new Error('No available product variant');

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: variant.id, quantity: 1 }],
        }),
      });
      const payload = await response.json().catch(() => ({}));

      if (!response.ok || payload.status) {
        throw new Error(payload.description || 'Unable to add the product');
      }

      const cart = await hsGetCart();
      hsDispatchCartUpdate(cart, 'product-form-fallback');
    } catch (error) {
      console.error('Product form fallback error:', error);
    } finally {
      if (addButton) addButton.disabled = false;
    }
  }

  async handleCartUpdate(event) {
    const detail = event.detail || {};
    const data = detail.data || {};
    const productId = data.productId;
    const source = String(data.source || detail.sourceId || '');

    if (!productId || data.didError || source.includes('upsell')) return;

    let cart;

    try {
      cart = detail.resource?.token ? detail.resource : await hsGetCart();
    } catch {
      return;
    }

    if (!cart?.items?.length) return;

    const token = String(cart.token || 'session');

    if (hsStorageGet(hsStorageKey('stage1-shown', token)) || this.shownToken === token) {
      return;
    }

    this.shownToken = token;
    const offers = await hsGetOffers(productId, cart, 'add', []);

    if (!offers.length) {
      this.shownToken = null;
      return;
    }

    hsStorageSet(hsStorageKey('stage1-shown', token), '1');
    hsStorageSet(
      hsStorageKey('stage1-offers', token),
      JSON.stringify(offers.map((offer) => ({ id: offer.id, handle: offer.handle })))
    );

    this.renderOffers(offers, hsSourceItem(cart, productId));

    const dialog = this.querySelector('dialog');

    if (dialog && !dialog.open) {
      dialog.showModal();
    }
  }

  handleClick(event) {
    const target = event.target instanceof Element ? event.target.closest('[data-upsell-close], [data-upsell-add]') : null;

    if (!target) return;

    if (target.matches('[data-upsell-close]')) {
      this.close();
      return;
    }

    if (target.matches('[data-upsell-add]')) {
      this.addToCart(target);
    }
  }

  renderOffers(offers, sourceItem) {
    const container = this.querySelector('[data-upsell-items]');
    const labels = {
      add: this.dataset.addLabel || 'Add',
      optionLabel: this.dataset.optionLabel || 'Choose an option',
      selectOption: this.dataset.selectOption || 'Select an option',
    };

    hsRenderOfferList(container, offers, labels);

    const added = this.querySelector('[data-upsell-added]');

    if (!added || !sourceItem) return;

    const image = sourceItem.image
      ? '<img src="' +
        hsEscape(sourceItem.image) +
        '" width="72" height="72" loading="lazy" alt="' +
        hsEscape(sourceItem.product_title) +
        '">'
      : '';

    added.hidden = false;
    added.innerHTML =
      '<span class="hs-upsell-added__image">' +
      image +
      '</span><span><strong>' +
      hsEscape(this.dataset.addedCopy || 'Added to your cart') +
      '</strong><span>' +
      hsEscape(sourceItem.product_title) +
      '</span></span>';
  }

  async addToCart(button) {
    if (button.disabled) return;

    const originalText = button.textContent;
    button.disabled = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: button.dataset.variantId, quantity: 1 }],
        }),
      });
      const payload = await response.json().catch(() => ({}));

      if (!response.ok || payload.status) {
        throw new Error(payload.description || 'Unable to add the product');
      }

      button.textContent = this.dataset.addedLabel || 'Added';
      const cart = await hsGetCart();
      hsDispatchCartUpdate(cart, 'upsell-popup');
    } catch {
      button.disabled = false;
      button.textContent = originalText;
    }
  }

  close() {
    const dialog = this.querySelector('dialog');
    hsCloseDialog(dialog);
  }
}

class CheckoutUpsell extends HTMLElement {
  constructor() {
    super();
    this.busy = false;
    this.proceeding = false;
    this.handleCheckoutClick = this.handleCheckoutClick.bind(this);
    this.handleCheckoutSubmit = this.handleCheckoutSubmit.bind(this);
    this.handleDialogClick = this.handleDialogClick.bind(this);
    this.handleDialogCancel = this.handleDialogCancel.bind(this);
  }

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    document.addEventListener('click', this.handleCheckoutClick, true);
    document.addEventListener('submit', this.handleCheckoutSubmit, true);
    this.addEventListener('click', this.handleDialogClick);
    this.dialog?.addEventListener('cancel', this.handleDialogCancel);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleCheckoutClick, true);
    document.removeEventListener('submit', this.handleCheckoutSubmit, true);
    this.removeEventListener('click', this.handleDialogClick);
    this.dialog?.removeEventListener('cancel', this.handleDialogCancel);
  }

  handleCheckoutClick(event) {
    if (this.busy || this.proceeding) return;

    const target = event.target instanceof Element
      ? event.target.closest('button[name="checkout"], input[name="checkout"]')
      : null;

    if (!target || target.closest('checkout-upsell-component')) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.openOffer();
  }

  handleCheckoutSubmit(event) {
    if (this.busy || this.proceeding) return;

    const form = event.target;

    if (!(form instanceof HTMLFormElement)) return;

    const submitter = event.submitter;
    const isCartForm = form.id === 'cart-form' || form.querySelector('[name="checkout"]');

    if (!isCartForm || (submitter && submitter.name !== 'checkout')) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.openOffer();
  }

  async openOffer() {
    if (this.busy || this.proceeding) return;

    this.busy = true;

    let cart;

    try {
      cart = await hsGetCart();
    } catch {
      this.proceed();
      return;
    }

    if (!cart?.items?.length) {
      this.proceed();
      return;
    }

    const token = String(cart.token || 'session');
    const shownKey = hsStorageKey('stage2-shown', token);

    if (hsStorageGet(shownKey)) {
      this.proceed();
      return;
    }

    const stage1Offers = (() => {
      try {
        return JSON.parse(hsStorageGet(hsStorageKey('stage1-offers', token)) || '[]');
      } catch {
        return [];
      }
    })();
    const excludedKeys = stage1Offers.flatMap((offer) => [offer.id, offer.handle]).filter(Boolean);
    const sourceItem = hsSourceItem(cart);
    const offers = await hsGetOffers(sourceItem?.product_id, cart, 'checkout', excludedKeys);

    hsStorageSet(shownKey, '1');

    if (!offers.length || !this.dialog) {
      this.proceed();
      return;
    }

    hsRenderOfferList(this.querySelector('[data-checkout-upsell-items]'), offers, {
      add: this.dataset.addLabel || 'Add & checkout',
      optionLabel: this.dataset.optionLabel || 'Choose an option',
      selectOption: this.dataset.selectOption || 'Select an option',
    });

    this.dialog.showModal();
  }

  handleDialogClick(event) {
    const target = event.target instanceof Element
      ? event.target.closest('[data-checkout-upsell-skip], [data-checkout-upsell-close], [data-upsell-add]')
      : null;

    if (!target) return;

    if (target.matches('[data-checkout-upsell-skip], [data-checkout-upsell-close]')) {
      this.proceed();
      return;
    }

    if (target.matches('[data-upsell-add]')) {
      this.addAndCheckout(target);
    }
  }

  handleDialogCancel(event) {
    event.preventDefault();
    this.proceed();
  }

  async addAndCheckout(button) {
    if (button.disabled || this.proceeding) return;

    const originalText = button.textContent;
    button.disabled = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: button.dataset.variantId, quantity: 1 }],
        }),
      });
      const payload = await response.json().catch(() => ({}));

      if (!response.ok || payload.status) {
        throw new Error(payload.description || 'Unable to add the product');
      }

      button.textContent = this.dataset.addedLabel || 'Added';
      this.proceed();
    } catch {
      button.disabled = false;
      button.textContent = originalText;
      const error = this.querySelector('[data-checkout-upsell-error]');

      if (error) {
        error.hidden = false;
      }
    }
  }

  proceed() {
    if (this.proceeding) return;

    this.proceeding = true;

    if (this.dialog?.open) {
      this.dialog.close();
    }

    window.location.assign(this.dataset.checkoutUrl || '/checkout');
  }
}

if (!customElements.get('upsell-popup-component')) {
  customElements.define('upsell-popup-component', UpsellPopup);
}

if (!customElements.get('checkout-upsell-component')) {
  customElements.define('checkout-upsell-component', CheckoutUpsell);
}
